import React, { useState, useEffect, useCallback } from "react";
import { Search, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { get } from "../api/client";
import { AuditEntry, Partner, UserPublic } from "../types";
import { useAuth } from "../hooks/useAuth";

const ACTION_OPTIONS = [
  { value: "", label: "Alle acties" },
  { value: "create", label: "Aangemaakt" },
  { value: "update", label: "Bijgewerkt" },
  { value: "delete", label: "Verwijderd" },
  { value: "status_change", label: "Status gewijzigd" },
  { value: "login", label: "Ingelogd" },
];

const actionLabel: Record<string, string> = {
  create: "Aangemaakt",
  update: "Bijgewerkt",
  delete: "Verwijderd",
  status_change: "Status gewijzigd",
  login: "Ingelogd",
};

const actionStyle: Record<string, string> = {
  create: "bg-green-100 text-green-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-600",
  status_change: "bg-yellow-100 text-yellow-700",
  login: "bg-slate-100 text-slate-600",
};

const PAGE_SIZE = 25;

function ActionBadge({ action }: { action: string }) {
  return (
    <span className={`pill ${actionStyle[action] ?? "bg-slate-100 text-slate-600"}`}>
      {actionLabel[action] ?? action}
    </span>
  );
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString("nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface AuditResponse {
  entries: AuditEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface KamUser {
  id: number;
  name: string;
  email: string;
}

export default function AuditTrail() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [partners, setPartners] = useState<Partner[]>([]);
  const [filterPartnerId, setFilterPartnerId] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // KAM filter
  const [kams, setKams] = useState<KamUser[]>([]);
  const [filterKamId, setFilterKamId] = useState<string>("");

  // Load partners for filter dropdown
  useEffect(() => {
    get<Partner[]>("/partners")
      .then(setPartners)
      .catch(() => {});
  }, []);

  // Load KAMs for filter dropdown (admin and kam roles only)
  useEffect(() => {
    if (user?.role === "admin" || user?.role === "kam") {
      get<KamUser[]>("/users/kams")
        .then(setKams)
        .catch(() => {});
    }
  }, [user]);

  const fetchEntries = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    if (filterPartnerId) {
      const partner = partners.find((p) => String(p.id) === filterPartnerId);
      if (partner) params.set("partner_name", partner.name);
    }
    if (filterAction) params.set("action", filterAction);
    if (filterSearch) params.set("functie", filterSearch);
    if (filterDateFrom) params.set("from", filterDateFrom);
    if (filterDateTo) params.set("to", filterDateTo);

    // KAM-groep filter
    if (user?.role === "admin" && filterKamId) {
      params.set("kam_id", filterKamId);
    }
    if (user?.role === "kam" && filterKamId === "mine") {
      params.set("my_hmps", "true");
    }

    get<AuditResponse | AuditEntry[]>(`/audit?${params.toString()}`)
      .then((res) => {
        // Handle both array response and paginated response
        if (Array.isArray(res)) {
          setEntries(res);
          setTotal(res.length);
        } else {
          setEntries(res.entries ?? []);
          setTotal(res.pagination?.total ?? 0);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [page, filterPartnerId, filterAction, filterSearch, filterDateFrom, filterDateTo, filterKamId, partners, user]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  function handleFilterChange() {
    setPage(1);
    fetchEntries();
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={15} className="text-slate-400" />
          <span className="text-sm font-medium text-slate-600">Filters</span>
        </div>
        {/* All filters stack vertically on mobile, multi-column on larger screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
          {/* Partner */}
          <select
            value={filterPartnerId}
            onChange={(e) => {
              setFilterPartnerId(e.target.value);
              setPage(1);
            }}
            className="select-field"
          >
            <option value="">Alle partners</option>
            {partners.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>

          {/* Actie */}
          <select
            value={filterAction}
            onChange={(e) => {
              setFilterAction(e.target.value);
              setPage(1);
            }}
            className="select-field"
          >
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {/* Zoek op functie */}
          <div className="relative sm:col-span-2 xl:col-span-2">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleFilterChange()}
              placeholder="Zoek op functie…"
              className="input-field pl-8"
            />
          </div>

          {/* Datum van */}
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => {
              setFilterDateFrom(e.target.value);
              setPage(1);
            }}
            className="input-field"
            title="Datum van"
          />

          {/* Datum tot */}
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => {
              setFilterDateTo(e.target.value);
              setPage(1);
            }}
            className="input-field"
            title="Datum tot"
          />

          {/* KAM-groep filter — admin: dropdown per KAM, KAM: eigen HMPs toggle */}
          {user?.role === "admin" && (
            <select
              value={filterKamId}
              onChange={(e) => {
                setFilterKamId(e.target.value);
                setPage(1);
              }}
              className="select-field"
            >
              <option value="">Alle KAMs</option>
              {kams.map((k) => (
                <option key={k.id} value={String(k.id)}>
                  {k.name}
                </option>
              ))}
            </select>
          )}
          {user?.role === "kam" && (
            <select
              value={filterKamId}
              onChange={(e) => {
                setFilterKamId(e.target.value);
                setPage(1);
              }}
              className="select-field"
            >
              <option value="">Alle wijzigingen</option>
              <option value="mine">Mijn HMPs</option>
            </select>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">
            <strong>Fout:</strong> {error}
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">
            Geen audit-records gevonden voor de geselecteerde filters.
          </div>
        ) : (
          <>
            {/* Mobile card view */}
            <div className="block md:hidden divide-y divide-slate-100">
              {entries.map((entry) => (
                <div key={entry.id} className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: "#65ADDD" }}
                      >
                        {entry.user_name?.charAt(0).toUpperCase() ?? "?"}
                      </div>
                      <span className="text-sm font-medium text-slate-700 truncate">
                        {entry.user_name}
                      </span>
                    </div>
                    <ActionBadge action={entry.action} />
                  </div>
                  {entry.vacature_functie && (
                    <p className="text-sm text-slate-700 font-medium">{entry.vacature_functie}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                    {entry.partner_name && (
                      <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-500">
                        {entry.partner_name}
                      </span>
                    )}
                    <span>{formatDate(entry.created_at)}</span>
                  </div>
                  {entry.details && (
                    <p className="text-xs text-slate-400 truncate">{entry.details}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      Datum
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Gebruiker
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Actie
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Vacature
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Partner
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                        {formatDate(entry.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                            style={{ backgroundColor: "#65ADDD" }}
                          >
                            {entry.user_name?.charAt(0).toUpperCase() ?? "?"}
                          </div>
                          <span className="text-slate-700 font-medium">{entry.user_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <ActionBadge action={entry.action} />
                      </td>
                      <td className="py-3 px-4 text-slate-700 font-medium">
                        {entry.vacature_functie ?? (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-500">
                        {entry.partner_name ?? (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-xs max-w-[200px] truncate">
                        {entry.details ?? (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && !error && total > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 gap-3">
            <span className="text-xs text-slate-500">
              {total} resultaten · Pagina {page} van {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 border border-slate-200 hover:border-slate-300 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
              >
                <ChevronLeft size={14} />
              </button>
              {/* Fewer page numbers on mobile */}
              {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                let p: number;
                if (totalPages <= 3) {
                  p = i + 1;
                } else if (page <= 2) {
                  p = i + 1;
                } else if (page >= totalPages - 1) {
                  p = totalPages - 2 + i;
                } else {
                  p = page - 1 + i;
                }
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-9 h-9 text-xs rounded border transition-colors md:w-8 md:h-7 ${
                      p === page
                        ? "bg-waves-blue text-white border-waves-blue"
                        : "hover:bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              {/* Show more page numbers on desktop */}
              <div className="hidden md:flex items-center gap-1">
                {totalPages > 3 && Array.from({ length: Math.min(5, totalPages) - 3 }, (_, i) => {
                  let p: number;
                  if (totalPages <= 5) {
                    p = i + 4;
                  } else if (page <= 3) {
                    p = i + 4;
                  } else if (page >= totalPages - 2) {
                    p = totalPages - 4 + i + 3;
                  } else {
                    p = page - 2 + i + 3;
                  }
                  if (p > totalPages) return null;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-7 text-xs rounded border transition-colors ${
                        p === page
                          ? "bg-waves-blue text-white border-waves-blue"
                          : "hover:bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 border border-slate-200 hover:border-slate-300 transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
